import React, { useCallback, useEffect, useRef, useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import { authService } from '../../services/AuthService';
import { GoogleCalendarOption } from '../../models/GoogleCalendar';
import { ORGANIZATION_GOOGLE_CALENDAR_HREF, GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED } from '../../utils/organizationSections';

export const ORG_GOOGLE_CALENDAR_NOT_CONNECTED = 'Organization Google Calendar is not connected';

const STORAGE_PREFIX = 'tutorGoogleCalendarSelection:';

export function readPersistedGoogleCalendarSelection(tutorId: string): GoogleCalendarOption | null {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${tutorId}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as GoogleCalendarOption;
    if (typeof parsed?.id === 'string' && typeof parsed?.name === 'string') {
      return parsed;
    }
  } catch {
    // Ignore malformed session storage entries.
  }
  return null;
}

export function persistGoogleCalendarSelection(
  tutorId: string,
  value: GoogleCalendarOption | null
): void {
  const key = `${STORAGE_PREFIX}${tutorId}`;
  if (!value) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

export type GoogleCalendarSelectionChangeMeta = {
  restored?: boolean;
};

interface TutorGoogleCalendarSectionProps {
  hasTutorProfile: boolean;
  tutorId: string | null;
  calendarId: string | null;
  value: GoogleCalendarOption | null;
  onChange: (value: GoogleCalendarOption | null, meta?: GoogleCalendarSelectionChangeMeta) => void;
  disabled?: boolean;
  compact?: boolean;
}

const TutorGoogleCalendarSection: React.FC<TutorGoogleCalendarSectionProps> = ({
  hasTutorProfile,
  tutorId,
  calendarId,
  value,
  onChange,
  disabled = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [googleCalendarOptions, setGoogleCalendarOptions] = useState<GoogleCalendarOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const canManageOrganization = authService.getRoles().includes('HOST');
  const restoredSelectionRef = useRef(false);

  const loadOptions = useCallback(async () => {
    // Host-only endpoint; tutor-only users get 403 and should see the "not connected" state instead.
    if (!canManageOrganization) {
      setGoogleCalendarOptions([]);
      return;
    }

    setOptionsLoading(true);
    try {
      const options = await hostApiClient.getGoogleCalendarOptions();
      setGoogleCalendarOptions(options);
    } catch {
      setGoogleCalendarOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  }, [canManageOrganization]);

  useEffect(() => {
    if (!GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED || !hasTutorProfile) {
      return;
    }
    void loadOptions();
  }, [hasTutorProfile, loadOptions]);

  useEffect(() => {
    restoredSelectionRef.current = false;
  }, [tutorId]);

  useEffect(() => {
    if (
      !GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED ||
      restoredSelectionRef.current ||
      value ||
      !calendarId?.trim() ||
      !tutorId?.trim() ||
      googleCalendarOptions.length === 0
    ) {
      return;
    }

    const storedSelection = readPersistedGoogleCalendarSelection(tutorId);
    if (!storedSelection) {
      return;
    }

    const matchedOption = googleCalendarOptions.find((option) => option.id === storedSelection.id);
    if (!matchedOption) {
      return;
    }

    restoredSelectionRef.current = true;
    onChange(matchedOption, { restored: true });
  }, [calendarId, googleCalendarOptions, onChange, tutorId, value]);

  if (!GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED || !hasTutorProfile) {
    return null;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        mb: compact ? 0 : 3,
        p: compact ? { xs: 1.5, md: 1.75 } : 2,
        borderRadius: 2,
      }}
    >
      <Typography
        variant={compact ? 'overline' : 'h6'}
        color={compact ? 'text.secondary' : 'text.primary'}
        sx={{ mb: compact ? 1 : 0.5, letterSpacing: compact ? 0.8 : undefined, display: 'block' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarTodayIcon fontSize="small" />
          {t('tutor.googleCalendar.title', 'Google Calendar')}
        </Box>
      </Typography>
      {!compact ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'tutor.googleCalendar.subtitle',
            'Choose which Google Calendar receives your lesson bookings. Your organization must connect Google Calendar first.'
          )}
        </Typography>
      ) : null}

      {optionsLoading ? (
        <Box display="flex" alignItems="center" gap={1.5}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {t('tutor.googleCalendar.loading', 'Loading Google Calendars...')}
          </Typography>
        </Box>
      ) : googleCalendarOptions.length === 0 ? (
        <Box
          sx={{
            backgroundColor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: compact ? 1.25 : 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 0.5 : 1 }}>
            {t(
              'tutor.googleCalendar.notEnabled',
              'Google Calendar is not connected for your organization.'
            )}
          </Typography>
          {!compact ? (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t(
                'tutor.googleCalendar.enableHint',
                'Ask your organization admin to connect Google Calendar under Integrations before you can link a calendar here.'
              )}
            </Typography>
          ) : null}
          {canManageOrganization ? (
            <Link href={ORGANIZATION_GOOGLE_CALENDAR_HREF} variant="body2" color="primary" sx={{ textDecoration: 'none', fontWeight: 'medium' }}>
              {t('tutor.googleCalendar.goToOrganizationSettings', 'Go to Google Calendar settings')} →
            </Link>
          ) : null}
        </Box>
      ) : (
        <FormControl fullWidth size="small">
          <InputLabel>{t('tutor.googleCalendar.selectLabel', 'Select Google Calendar')}</InputLabel>
          <Select
            value={value?.id ?? ''}
            label={t('tutor.googleCalendar.selectLabel', 'Select Google Calendar')}
            onChange={(e) => {
              const selectedId = e.target.value;
              const selectedOption = googleCalendarOptions.find((opt) => opt.id === selectedId) ?? null;
              onChange(selectedOption);
            }}
            disabled={disabled}
          >
            <MenuItem value="">
              <em>{t('tutor.googleCalendar.noCalendarSelected', 'No calendar selected')}</em>
            </MenuItem>
            {googleCalendarOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Card>
  );
};

export default TutorGoogleCalendarSection;
